import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardMedia,
  CardActions,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { sekurantenAPI } from '../../services/api';
import Grid from '@mui/material/Grid';
import { FileUpload } from '../FileUpload/FileUpload';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface SekurantenDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  sekurantenProjektId: number;
  sekurant?: any | null;
}

interface SekurantenFormData {
  number: string;
  description: string;
  photos: File[];
  verantwortlicher_monteur?: string;
  hersteller_anschlageinrichtung?: string;
  typ_anschlageinrichtung?: string;
  hersteller_befestigungsmittel?: string;
}

interface PhotoPreview {
  file: File;
  url: string;
}

export const SekurantenDialog: React.FC<SekurantenDialogProps> = ({
  open,
  onClose,
  onSave,
  sekurantenProjektId,
  sekurant
}) => {
  const [formData, setFormData] = useState<SekurantenFormData>({
    number: '',
    description: '',
    photos: [],
    verantwortlicher_monteur: '',
    hersteller_anschlageinrichtung: '',
    typ_anschlageinrichtung: '',
    hersteller_befestigungsmittel: '',
  });
  const [photoPreviews, setPhotoPreviews] = useState<PhotoPreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load data when editing an existing sekurant
  useEffect(() => {
    if (sekurant) {
      setFormData({
        number: sekurant.number || '',
        description: sekurant.description || '',
        photos: [],
        verantwortlicher_monteur: sekurant.verantwortlicher_monteur || '',
        hersteller_anschlageinrichtung: sekurant.hersteller_anschlageinrichtung || '',
        typ_anschlageinrichtung: sekurant.typ_anschlageinrichtung || '',
        hersteller_befestigungsmittel: sekurant.hersteller_befestigungsmittel || '',
      });
    } else {
      // Reset form when creating new sekurant
      setFormData({
        number: '',
        description: '',
        photos: [],
        verantwortlicher_monteur: '',
        hersteller_anschlageinrichtung: '',
        typ_anschlageinrichtung: '',
        hersteller_befestigungsmittel: '',
      });
    }
    setPhotoPreviews([]);
    setError(null);
  }, [sekurant, open]);

  const handleInputChange = (field: keyof SekurantenFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handlePhotosChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    // Validate file types and sizes
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setError('Nur JPEG und PNG Dateien sind erlaubt');
        return;
      }
      
      if (file.size > maxSize) {
        setError('Datei ist zu groß. Maximum 10MB');
        return;
      }
    }

    // Add new photos to existing ones
    const newPhotos = [...formData.photos, ...files];
    setFormData(prev => ({ ...prev, photos: newPhotos }));
    
    // Create previews for new files
    const newPreviews: PhotoPreview[] = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        newPreviews.push({
          file,
          url: reader.result as string
        });
        
        if (newPreviews.length === files.length) {
          setPhotoPreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
    
    setError(null);
    
    // Reset input value to allow selecting the same files again
    event.target.value = '';
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.number.trim()) {
      setError('Sekuranten-Nummer ist erforderlich');
      return;
    }
    if (!formData.description.trim()) {
      setError('Beschreibung ist erforderlich');
      return;
    }
    if (formData.photos.length === 0) {
      setError('Mindestens ein Foto ist erforderlich');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('sekuranten_projekt_id', sekurantenProjektId.toString());
      formDataToSend.append('number', formData.number);
      formDataToSend.append('description', formData.description);
      if (formData.verantwortlicher_monteur) formDataToSend.append('verantwortlicher_monteur', formData.verantwortlicher_monteur);
      if (formData.hersteller_anschlageinrichtung) formDataToSend.append('hersteller_anschlageinrichtung', formData.hersteller_anschlageinrichtung);
      if (formData.typ_anschlageinrichtung) formDataToSend.append('typ_anschlageinrichtung', formData.typ_anschlageinrichtung);
      if (formData.hersteller_befestigungsmittel) formDataToSend.append('hersteller_befestigungsmittel', formData.hersteller_befestigungsmittel);
      // Append all photos
      formData.photos.forEach(photo => {
        formDataToSend.append('photos', photo);
      });
      await sekurantenAPI.createSekurant(formDataToSend);
      setFormData({ number: '', description: '', photos: [], verantwortlicher_monteur: '', hersteller_anschlageinrichtung: '', typ_anschlageinrichtung: '', hersteller_befestigungsmittel: '' });
      setPhotoPreviews([]);
      onSave();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ number: '', description: '', photos: [], verantwortlicher_monteur: '', hersteller_anschlageinrichtung: '', typ_anschlageinrichtung: '', hersteller_befestigungsmittel: '' });
      setPhotoPreviews([]);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Neuen Sekuranten hinzufügen
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Sekuranten-Nummer"
            value={formData.number}
            onChange={handleInputChange('number')}
            margin="normal"
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Beschreibung"
            value={formData.description}
            onChange={handleInputChange('description')}
            margin="normal"
            multiline
            rows={3}
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Name des verantwortlichen Monteurs"
            value={formData.verantwortlicher_monteur}
            onChange={handleInputChange('verantwortlicher_monteur')}
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Hersteller der Anschlageinrichtung"
            value={formData.hersteller_anschlageinrichtung}
            onChange={handleInputChange('hersteller_anschlageinrichtung')}
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Typ/Modell/Artikelnummer der Anschlageinrichtung"
            value={formData.typ_anschlageinrichtung}
            onChange={handleInputChange('typ_anschlageinrichtung')}
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Hersteller und Typ der Befestigungsmittel"
            value={formData.hersteller_befestigungsmittel}
            onChange={handleInputChange('hersteller_befestigungsmittel')}
            margin="normal"
            disabled={loading}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Fotos hochladen * ({formData.photos.length} ausgewählt)
            </Typography>
            
            {/* iOS-kompatibler Foto-Upload ohne accept-Attribute */}
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              sx={{ mb: 2 }}
              disabled={loading}
              onClick={() => {
                // Erstelle einen versteckten File-Input für iOS-Kompatibilität
                const input = document.createElement('input');
                input.type = 'file';
                input.multiple = true;
                // KEINE accept-Attribute - das verursacht iOS-Probleme
                input.style.display = 'none';
                input.onchange = (e: Event) => {
                  const target = e.target as HTMLInputElement;
                  const fileList = target.files ? Array.from(target.files) : [];
                  // Direkte Verarbeitung der Dateien ohne Event-Simulation
                  if (fileList.length > 0) {
                    // Validate file types and sizes
                    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
                    const maxSize = 10 * 1024 * 1024; // 10MB
                    
                    for (const file of fileList) {
                      if (!allowedTypes.includes(file.type)) {
                        setError('Nur JPEG und PNG Dateien sind erlaubt');
                        return;
                      }
                      
                      if (file.size > maxSize) {
                        setError('Datei ist zu groß. Maximum 10MB');
                        return;
                      }
                    }

                    // Add new photos to existing ones
                    const newPhotos = [...formData.photos, ...fileList];
                    setFormData(prev => ({ ...prev, photos: newPhotos }));
                    
                    // Create previews for new files
                    const newPreviews: PhotoPreview[] = [];
                    fileList.forEach(file => {
                      const reader = new FileReader();
                      reader.onload = () => {
                        newPreviews.push({
                          file,
                          url: reader.result as string
                        });
                        
                        if (newPreviews.length === fileList.length) {
                          setPhotoPreviews(prev => [...prev, ...newPreviews]);
                        }
                      };
                      reader.readAsDataURL(file);
                    });
                    
                    setError(null);
                  }
                };
                document.body.appendChild(input);
                input.click();
                document.body.removeChild(input);
              }}
            >
              Fotos hinzufügen
            </Button>

            {/* Photo Previews */}
            {photoPreviews.length > 0 && (
              <Grid sx={{ mt: 1 }}>
                {photoPreviews.map((preview, index) => (
                  <Grid key={index}>
                    <Card sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="100"
                        image={preview.url}
                        alt={`Foto ${index + 1}`}
                      />
                      <IconButton
                        sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
                        size="small"
                        onClick={() => removePhoto(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={loading}
        >
          Abbrechen
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Speichern...
            </>
          ) : (
            'Speichern'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 